class Draggable {
  #dragObject = null;

  #pos_x;

  #pos_y;

  #ofs_x;

  #ofs_y;

  #ctrl;

  #dragHandler;

  #options;

  constructor(ctrl, options) {
    this.#ctrl = ctrl;

    this.#options = Object.assign({
      exclude: [
        'INPUT',
        'TEXTAREA',
        'SELECT',
        'A',
        'BUTTON',
      ],
      handler: null,
      ondrag: null,
    }, options);

    if (this.#options.handler) {
      this.#dragHandler = ctrl.querySelector(this.#options.handler);
    } else {
      this.#dragHandler = ctrl;
    }

    if (this.#dragHandler) {
      this.#dragHandler.style.cursor = 'move';
      this.#dragHandler.style.zIndex = 1;

      this.#prepareControl(ctrl);

      this.#dragHandler.addEventListener('mousedown', this.#onPointerDown);
      this.#dragHandler.addEventListener('touchstart', this.#onTouchStart, {
        passive: false,
      });

      window.addEventListener('mousemove', this.#onPointerMove);
      window.addEventListener('mouseup', this.#onPointerUp);
      window.addEventListener('touchmove', this.#onPointerMove, {
        passive: false,
      });
      window.addEventListener('touchend', this.#onPointerUp);
    }
  }

  destroy() {
    this.#upHandler();

    if (this.#dragHandler) {
      this.#dragHandler.removeEventListener('mousedown', this.#onPointerDown);
      this.#dragHandler.removeEventListener('touchstart', this.#onTouchStart);
    }

    window.removeEventListener('mousemove', this.#onPointerMove);
    window.removeEventListener('mouseup', this.#onPointerUp);
    window.removeEventListener('touchmove', this.#onPointerMove);
    window.removeEventListener('touchend', this.#onPointerUp);
  }

  #position(element) {
    let top = 0;
    let left = 0;

    do {
      top += element.offsetTop || 0;
      left += element.offsetLeft || 0;
      element = element.offsetParent;
    } while (element);

    return {
      top,
      left,
    };
  }

  #prepareControl(element) {
    const position = this.#position(element);

    element.style.position = 'fixed';
    element.style.left = `${position.left}px`;
    element.style.top = `${position.top}px`;
    element.style.marginTop = '0px';
    element.style.marginLeft = '0px';
  }

  #setPosition(element, left, top) {
    element.style.marginTop = '0px';
    element.style.marginLeft = '0px';
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.right = '';
    element.style.bottom = '';
  }

  #onPointerDown = (event) => {
    this.#downHandler(event);
  };

  #onTouchStart = (event) => {
    this.#downHandler(event, true);
  };

  #onPointerMove = (event) => {
    this.#moveHandler(event);
  };

  #onPointerUp = () => {
    this.#upHandler();
  };

  #moveHandler(e) {
    if (this.#dragObject === null) {
      return;
    }

    const pageX = e.pageX ?? e.touches[0].pageX;
    const pageY = e.pageY ?? e.touches[0].pageY;
    let left = pageX - this.#pos_x - this.#ofs_x - document.body.scrollLeft;
    let top = pageY - this.#pos_y - this.#ofs_y - document.body.scrollTop;

    if (top < 0) {
      top = 0;
    }
    if (top > window.innerHeight) {
      top = window.innerHeight - 40;
    }
    if (left < 0) {
      left = 0;
    }

    this.#setPosition(this.#dragObject, left, top);

    if (typeof this.#options.ondrag === 'function') {
      this.#options.ondrag.call(e);
    }
  }

  #upHandler() {
    this.#dragObject = null;
  }

  #downHandler(e, touchEvent) {
    if ((e.button !== 0) && !touchEvent) {
      return;
    }

    const target = e.target || e.srcElement;
    const parent = target.parentNode;

    if (!target || (this.#options.exclude.indexOf(target.tagName.toUpperCase()) !== -1)) {
      return;
    }

    if (parent && (this.#options.exclude.indexOf(parent.tagName.toUpperCase()) !== -1)) {
      return;
    }

    this.#dragObject = this.#ctrl;

    const pageX = e.pageX || e.touches[0].pageX;
    const pageY = e.pageY || e.touches[0].pageY;

    this.#ofs_x = this.#dragObject.getBoundingClientRect().left - this.#dragObject.offsetLeft;
    this.#ofs_y = this.#dragObject.getBoundingClientRect().top - this.#dragObject.offsetTop;

    this.#pos_x = pageX - (this.#dragObject.getBoundingClientRect().left + document.body.scrollLeft);
    this.#pos_y = pageY - (this.#dragObject.getBoundingClientRect().top + document.body.scrollTop);

    e.preventDefault();
  }
}

export default Draggable;
